import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api-graphql";
import { API } from "aws-amplify";

import {
  CreateCheckInInput,
  CreateCheckInMutation,
  CreateCheckInMutationVariables,
  QueryCheckInsByUserSubQuery,
  QueryCheckInsByUserSubQueryVariables,
  QueryGoalsByUserSubQuery,
  QueryGoalsByUserSubQueryVariables,
  UpdateGoalInput,
  UpdateGoalMutation,
  UpdateGoalMutationVariables,
  CreateHabitInput,
  CreateHabitMutation,
  CreateHabitMutationVariables,
  ListHabitCategoriesQueryVariables,
  ListHabitCategoriesQuery,
  QueryHabitsByUserSubQueryVariables,
  QueryHabitsByUserSubQuery,
  CreateGoalInput,
  CreateGoalMutationVariables,
  CreateGoalMutation,
  UpdateCheckInInput,
  UpdateCheckInMutationVariables,
  UpdateCheckInMutation,
  DeleteGoalMutationVariables,
  DeleteGoalMutation,
  DeleteGoalInput,
} from "../../api/graphql/API";
import {
  createCheckIn,
  updateGoal,
  createHabit,
  createGoal,
  updateCheckIn,
  deleteGoal,
} from "../../graphql/mutations";
import {
  listHabitCategories,
  queryCheckInsByUserSub,
  queryGoalsByUserSub,
  queryHabitsByUserSub,
} from "../../graphql/queries";

export const apiGetHabitCategories = async () => {
  const _data = (await API.graphql({
    query: listHabitCategories,
    variables: {} as ListHabitCategoriesQueryVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: ListHabitCategoriesQuery };
  if (!_data || !_data.data || !_data.data.listHabitCategories) {
    throw Error("There are no categories");
  }
  return _data.data.listHabitCategories;
};

export const apiGetHabits = async (user_sub: String) => {
  const _data = (await API.graphql({
    query: queryHabitsByUserSub,
    variables: { user_sub } as QueryHabitsByUserSubQueryVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: QueryHabitsByUserSubQuery };
  if (!_data || !_data.data || !_data.data.queryHabitsByUserSub) {
    throw Error("There are no habits");
  }
  return _data.data.queryHabitsByUserSub;
};

export const apiCreateNewHabit = async (habit: CreateHabitInput) => {
  const _data = (await API.graphql({
    query: createHabit,
    variables: {
      input: {
        categoryId: habit.categoryId,
        description: habit.description,
        user_sub: habit.user_sub,
        iconName: habit?.iconName,
      },
    } as CreateHabitMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: CreateHabitMutation };
  if (!_data || !_data.data || !_data.data.createHabit) {
    throw Error("No data - while updating user activities");
  }
  return _data.data.createHabit;
};

export const apiQueryUserGoals = async (user_sub: String) => {
  const _data = (await API.graphql({
    query: queryGoalsByUserSub,
    variables: { user_sub } as QueryGoalsByUserSubQueryVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: QueryGoalsByUserSubQuery };
  if (!_data || !_data.data || !_data.data.queryGoalsByUserSub) {
    throw Error("There are no categories");
  }
  return _data.data.queryGoalsByUserSub;
};

export const apiQueryUserCheckIns = async (user_sub: String) => {
  const _data = (await API.graphql({
    query: queryCheckInsByUserSub,
    variables: { user_sub } as QueryCheckInsByUserSubQueryVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: QueryCheckInsByUserSubQuery };

  if (!_data || !_data.data || !_data.data.queryCheckInsByUserSub) {
    throw Error("There are no Check ins");
  }
  return _data.data.queryCheckInsByUserSub;
};

export const apiCreateNewCheckIn = async (checkIn: CreateCheckInInput) => {
  const _data = (await API.graphql({
    query: createCheckIn,
    variables: {
      input: {
        id: checkIn.id,
        goalId: checkIn.goalId,
        dateTime: checkIn.dateTime,
        user_sub: checkIn.user_sub,
        goalCompleted: checkIn.goalCompleted,
      },
    } as CreateCheckInMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: CreateCheckInMutation };
  if (!_data || !_data.data || !_data.data.createCheckIn) {
    throw Error("No data - while updating user activities");
  }
  return _data.data.createCheckIn;
};

export const apiUpdateCheckIn = async (checkIn: UpdateCheckInInput) => {
  const _data = (await API.graphql({
    query: updateCheckIn,
    variables: {
      input: {
        id: checkIn.id,
        goalId: checkIn.goalId,
        dateTime: checkIn.dateTime,
        user_sub: checkIn.user_sub,
        goalCompleted: checkIn.goalCompleted,
      },
    } as UpdateCheckInMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: UpdateCheckInMutation };
  if (!_data || !_data.data || !_data.data.updateCheckIn) {
    throw Error("Cannot update goal checkin");
  }
  return _data.data.updateCheckIn;
};

export const apiCreateNewGoal = async (goal: CreateGoalInput) => {
  const _data = (await API.graphql({
    query: createGoal,
    variables: {
      input: {
        forNoPeriods: goal.forNoPeriods,
        response: goal.response,
        goalId: goal.goalId,
        noTimesInPeriod: goal.noTimesInPeriod,
        obstacle: goal.obstacle,
        monthPeriod: goal.monthPeriod,
        yearPeriod: goal.yearPeriod,
        solutions: goal.solutions,
        selectedSolution: goal.selectedSolution,
        startDate: goal.startDate,
        user_sub: goal.user_sub,
        title: goal.title,
        completed_goal: goal.completed_goal,
      },
    } as CreateGoalMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: CreateGoalMutation };
  if (!_data || !_data.data || !_data.data.createGoal) {
    throw Error("No data - while updating user activities");
  }
  return _data.data.createGoal;
};

export const apiUpdateGoal = async (goal: UpdateGoalInput) => {
  const _data = (await API.graphql({
    query: updateGoal,
    variables: {
      input: {
        forNoPeriods: goal.forNoPeriods,
        response: goal.response,
        goalId: goal.goalId,
        id: goal.id,
        noTimesInPeriod: goal.noTimesInPeriod,
        obstacle: goal.obstacle,
        monthPeriod: goal.monthPeriod,
        yearPeriod: goal.yearPeriod,
        solutions: goal.solutions,
        selectedSolution: goal.selectedSolution,
        startDate: goal.startDate,
        user_sub: goal.user_sub,
        title: goal.title,
        completed_goal: goal.completed_goal,
      },
    } as UpdateGoalMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: UpdateGoalMutation };
  if (!_data || !_data.data || !_data.data.updateGoal) {
    throw Error("Cannot update goal");
  }
  return _data.data.updateGoal;
};
export const apiDeleteGoal = async (goal: DeleteGoalInput) => {
  const _data = (await API.graphql({
    query: deleteGoal,
    variables: {
      input: {
        id: goal.id,

        // completed_goal: goal.completed_goal,
      },
    } as DeleteGoalMutationVariables,
    authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
  })) as { data: DeleteGoalMutation };
  if (!_data || !_data.data || !_data.data.deleteGoal) {
    throw Error("Cannot delete goal");
  }
  return _data.data.deleteGoal;
};
